import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface ControlTypeData {
  control_type_code: string;
  disabled: boolean;
}

interface ControlTypeDropdownProps {
  selectedControlType: string;
  onControlTypeChange: (
    selectedControlType: string,
    selectedControlTypeObject?: ControlTypeData
  ) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const ControlTypeDropdown: React.FC<ControlTypeDropdownProps> = ({
  selectedControlType,
  onControlTypeChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [controlTypeData, setControlTypeData] = useState<ControlTypeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      const startTime = performance.now();
      try {
        const data = await fetchWithCache("control-type", "/ref-data/control-type");
        setControlTypeData(data);

        datadogRum.addAction("Fetched Control Types (Cached/Live)", {
          controlTypeCount: data.length,
        });
      } catch (err) {
        setError("Failed to fetch control types");
        datadogRum.addError("Control Type Fetch Failed", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("Control Type Fetch Duration", {
          duration: endTime - startTime,
        });
      }
    };

    loadData();
  }, []);

  const options = controlTypeData.map((item) => ({
    value: item.control_type_code,
    label: item.control_type_code,
  }));

  const handleChange = (value: string) => {
    const selected = controlTypeData.find((item) => item.control_type_code === value);
    if (selected) {
      onControlTypeChange(value, selected);
      datadogRum.addAction("Control Type Selected", selected);
    }
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedControlType}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading Control Types..." : error ?? "Select Control Type"}
      onFocus={onFocus}
    />
  );
};

export default ControlTypeDropdown;
